import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Phone, MapPin, Clock, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { countryCodes } from '../mock';
import { toast } from 'sonner';
import { getGeoLocation, submitLead } from '../services/api';

export const Contact = () => {
  const navigate = useNavigate();
  const [geoData, setGeoData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+1',
    country: 'US',
    contactNumber: '',
    message: ''
  });

  // Fetch geolocation on component mount
  useEffect(() => {
    fetchGeoLocation();
  }, []);

  const fetchGeoLocation = async () => {
    const result = await getGeoLocation();
    if (result.success) {
      setGeoData(result.data);
      const countryCode = countryCodes.find(c => c.country === result.data.countryCode);
      if (countryCode) {
        setFormData(prev => ({ 
          ...prev, 
          countryCode: countryCode.code,
          country: result.data.countryCode 
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const dialCode = parseInt(formData.countryCode.replace('+', ''));
      
      // Clean phone number - remove all non-numeric characters
      const cleanedPhone = formData.contactNumber.replace(/\D/g, '');
      
      // Validate phone number length (should be at least 7 digits)
      if (cleanedPhone.length < 7) {
        toast.error('Please enter a valid phone number (at least 7 digits)');
        setIsLoading(false);
        return;
      }

      const leadData = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        countryDialCode: dialCode,
        country: formData.country,
        phoneNumber: cleanedPhone,
        description: formData.message.trim() || 'Contact form submission'
      };

      const result = await submitLead(leadData, geoData);

      if (result.success) {
        toast.success('Thank you! Your application has been submitted successfully.');
        
        setFormData({
          name: '',
          email: '',
          countryCode: '+1',
          country: 'US',
          contactNumber: '',
          message: ''
        });
        
        // Redirect to thank you page with customer ID
        setTimeout(() => {
          navigate(result.redirectUrl);
        }, 1000);
      } else {
        toast.error(result.error || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="pt-20">
      {/* Hero Section with Background */}
      <section className="relative bg-gradient-to-br from-blue-600 to-blue-700 text-white py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://images.unsplash.com/photo-1599163666602-ef737d996c16?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMHBhdHRlcm5zfGVufDB8fHx8MTc2MjU0OTAwMnww&ixlib=rb-4.1.0&q=85" 
            alt="Background" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-6xl font-bold mb-6">Contact Us</h1>
            <p className="text-2xl text-white/95">
              Get in touch with our team of trademark experts
            </p>
          </div>
        </div>
      </section>

      {/* Hero Image Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl h-96">
              <img 
                src="https://images.pexels.com/photos/3727463/pexels-photo-3727463.jpeg" 
                alt="Professional consultation" 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-transparent flex items-center">
                <div className="text-white p-12 max-w-2xl">
                  <h2 className="text-4xl font-bold mb-4">Let's Discuss Your Trademark Needs</h2>
                  <p className="text-xl text-white/90">Our experienced team is ready to guide you through every step of the trademark registration process</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 max-w-7xl mx-auto">
            {/* Contact Information */}
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Get In Touch</h2>
              <p className="text-gray-600 mb-10 text-xl leading-relaxed">
                Have questions about trademark registration? Our team is here to help you protect your brand.
              </p>

              <div className="space-y-8">
                <div className="flex items-start gap-6 bg-white p-6 rounded-2xl shadow-lg">
                  <div className="bg-blue-100 w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="text-blue-600" size={28} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2 text-xl">Email</h3>
                    <p className="text-gray-600 text-lg">info@trademarkfrontline.com</p>
                    <p className="text-sm text-gray-500 mt-1">We respond within 24 hours</p>
                  </div>
                </div>

                <div className="flex items-start gap-6 bg-white p-6 rounded-2xl shadow-lg">
                  <div className="bg-orange-100 w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="text-orange-600" size={28} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2 text-xl">Phone</h3>
                    <p className="text-gray-600 text-lg">+1 (844) 627-0636</p>
                    <p className="text-sm text-gray-500 mt-1">Monday - Friday, 9AM - 6PM EST</p>
                  </div>
                </div>

                <div className="flex items-start gap-6 bg-white p-6 rounded-2xl shadow-lg">
                  <div className="bg-blue-100 w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-blue-600" size={28} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2 text-xl">Office</h3>
                    <p className="text-gray-600 text-lg">515 Flower St<br />Los Angeles, CA 90071, USA</p>
                    <p className="text-sm text-gray-500 mt-1">Visit by appointment only</p>
                  </div>
                </div>

                <div className="flex items-start gap-6 bg-white p-6 rounded-2xl shadow-lg">
                  <div className="bg-orange-100 w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="text-orange-600" size={28} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2 text-xl">Business Hours</h3>
                    <p className="text-gray-600">
                      Monday - Friday: 9:00 AM - 6:00 PM<br />
                      Saturday: 10:00 AM - 2:00 PM<br />
                      Sunday: Closed
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-3xl p-10 shadow-2xl border border-gray-100">
              <h3 className="text-3xl font-bold text-gray-900 mb-8">Send Us a Message</h3>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                  <Input
                    required
                    disabled={isLoading}
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Your full name"
                    className="h-12"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                  <Input
                    type="email"
                    required
                    disabled={isLoading}
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="your@email.com"
                    className="h-12"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Contact Number *</label>
                  <div className="flex gap-3">
                    <Select
                      value={formData.countryCode}
                      onValueChange={(value) => {
                        const selected = countryCodes.find(c => c.code === value);
                        setFormData({ 
                          ...formData, 
                          countryCode: value,
                          country: selected?.country || 'US'
                        });
                      }}
                      disabled={isLoading}
                    >
                      <SelectTrigger className="w-32 h-12">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {countryCodes.map((item, index) => (
                          <SelectItem key={index} value={item.code}>
                            {item.code}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      type="tel"
                      required
                      disabled={isLoading}
                      value={formData.contactNumber}
                      onChange={(e) => {
                        // Remove all non-numeric characters and limit to 15 digits
                        const value = e.target.value.replace(/\D/g, '').slice(0, 15);
                        setFormData({ ...formData, contactNumber: value });
                      }}
                      onKeyPress={(e) => {
                        // Prevent non-numeric key presses
                        if (!/[0-9]/.test(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      placeholder="Phone number"
                      maxLength={15}
                      pattern="[0-9]*"
                      inputMode="numeric"
                      className="flex-1 h-12"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                  <Textarea
                    required
                    disabled={isLoading}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Tell us about your needs..."
                    rows={6}
                    className="resize-none"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-primary hover:bg-primary/90 h-14 text-lg"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Send Message'
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};